import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Spinner,
  SpinnerSize,
  Text,
  ContextualMenu,
  IContextualMenuItem,
  Icon,
  IconButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Selection,
  IColumn,
  IDetailsList,
  ScrollToMode,
  IObjectWithKey,
  CheckboxVisibility,
  IDetailsRowProps,
  ScrollablePane,
  Sticky,
  StickyPositionType,
  IDetailsHeaderProps,
} from '@fluentui/react';
import { useFileBrowserStore } from '@/hooks';
import { BlobItem } from '@/types';
import { formatDate, formatItemDateTooltip, getItemDateDisplay } from '@/config';
import { parentFolderPath } from '@/utils/tree';
import { FileThumbnail } from './FileThumbnail';
import { ConfirmDialog } from './ConfirmDialog';
import { AttachmentIndicator } from './AttachmentIndicator';

function FileListInner() {
  // Granular selectors so the list only re-renders when these change, NOT on
  // every drag move or upload-progress tick (the whole-store subscription was
  // re-rendering the entire tree and causing the drop lag). The drag-over
  // highlight is handled imperatively in DropZone, so the list does not
  // subscribe to dragOverPath at all.
  const isLoading = useFileBrowserStore((s) => s.isLoading);
  const error = useFileBrowserStore((s) => s.error);
  const selectedItems = useFileBrowserStore((s) => s.selectedItems);
  const setPreviewItem = useFileBrowserStore((s) => s.setPreviewItem);
  const openMoveDialog = useFileBrowserStore((s) => s.openMoveDialog);
  const openRenameDialog = useFileBrowserStore((s) => s.openRenameDialog);
  const downloadItem = useFileBrowserStore((s) => s.downloadItem);
  const openFile = useFileBrowserStore((s) => s.openFile);
  const copyLink = useFileBrowserStore((s) => s.copyLink);
  const deleteItem = useFileBrowserStore((s) => s.deleteItem);
  const isSearching = useFileBrowserStore((s) => s.isSearching);
  const sortBy = useFileBrowserStore((s) => s.sortBy);
  const sortOrder = useFileBrowserStore((s) => s.sortOrder);
  const setSortBy = useFileBrowserStore((s) => s.setSortBy);
  const toggleSortOrder = useFileBrowserStore((s) => s.toggleSortOrder);
  const currentSiteId = useFileBrowserStore((s) => s.currentSiteId);
  const folderFavorites = useFileBrowserStore((s) => s.folderFavorites);
  const addFolderFavorite = useFileBrowserStore((s) => s.addFolderFavorite);
  const removeFolderFavorite = useFileBrowserStore((s) => s.removeFolderFavorite);
  const browserView = useFileBrowserStore((s) => s.browserView);

  // Stable reference (rebuilt only on tree changes), so the memos below hold
  // across unrelated updates like dragOverPath changing during a drag. Search
  // now filters the tree in place, so this is always the (possibly pruned) tree.
  const visibleRows = useFileBrowserStore((s) => s.visibleRows);
  const toggleExpand = useFileBrowserStore((s) => s.toggleExpand);
  const loadingPaths = useFileBrowserStore((s) => s.loadingPaths);
  const treeLoading = useFileBrowserStore((s) => s.treeLoading);
  const filteredItems = useMemo(() => visibleRows.map((r) => r.item), [visibleRows]);
  // Indent/expansion lookup by path for the name cell.
  const rowMeta = useMemo(() => new Map(visibleRows.map((r) => [r.item.path, r])), [visibleRows]);
  const inFavorites = browserView === 'favorites';
  const favoritePaths = useMemo(
    () => new Set(folderFavorites.filter((f) => f.siteId === currentSiteId).map((f) => f.path)),
    [folderFavorites, currentSiteId],
  );

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: BlobItem | null;
  }>({ isOpen: false, item: null });

  // Context menu state for row right-click
  const [contextMenu, setContextMenu] = useState<{
    item: BlobItem;
    target: { x: number; y: number };
  } | null>(null);

  // ---------------------------------------------------------------
  // Selection: the Fluent Selection object owns checkbox/shift-click/ctrl
  // mechanics and pushes the result into the store; store-side changes
  // (Esc clear, Ctrl+A shortcut, post-delete reset) are reconciled back.
  // The guard ref stops the two directions from feeding each other.
  // ---------------------------------------------------------------
  const syncingFromStore = useRef(false);
  const selection = useMemo(
    () =>
      new Selection({
        getKey: (item) => (item as BlobItem).path,
        onSelectionChanged: () => {
          if (syncingFromStore.current) return;
          const paths = (selection.getSelection() as BlobItem[]).map((i) => i.path);
          useFileBrowserStore.getState().setSelection(paths);
        },
      }),
    [],
  );

  useEffect(() => {
    syncingFromStore.current = true;
    try {
      // Keep selection by key across refreshes; then reconcile to the store.
      selection.setItems(filteredItems as IObjectWithKey[], false);
      const selectedNow = new Set((selection.getSelection() as BlobItem[]).map((i) => i.path));
      const wanted = selectedItems;
      const same = selectedNow.size === wanted.size && [...selectedNow].every((p) => wanted.has(p));
      if (!same) {
        selection.setChangeEvents(false);
        selection.setAllSelected(false);
        wanted.forEach((path) => selection.setKeySelected(path, true, false));
        selection.setChangeEvents(true);
      }
    } finally {
      syncingFromStore.current = false;
    }
  }, [filteredItems, selectedItems, selection]);

  const handleItemInvoked = useCallback(
    (item?: BlobItem) => {
      if (!item) return;
      if (item.isFolder) {
        void toggleExpand(item.path);
      } else {
        setPreviewItem(item);
      }
    },
    [toggleExpand, setPreviewItem],
  );

  const showDeleteConfirm = useCallback((item: BlobItem) => {
    setConfirmDialog({ isOpen: true, item });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    // Close immediately - deleteItem drives the list's loading overlay, so
    // the user gets feedback instead of a frozen dialog.
    const item = confirmDialog.item;
    setConfirmDialog({ isOpen: false, item: null });
    if (item) {
      await deleteItem(item);
    }
  }, [confirmDialog.item, deleteItem]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDialog({ isOpen: false, item: null });
  }, []);

  const getMenuItems = useCallback(
    (item: BlobItem): IContextualMenuItem[] => {
      if (item.isFolder) {
        const favorite = folderFavorites.some(
          (entry) => entry.siteId === currentSiteId && entry.path === item.path,
        );
        return [
          {
            key: 'open',
            text: 'Open',
            iconProps: { iconName: 'FolderOpen' },
            onClick: () => {
              void toggleExpand(item.path);
              setContextMenu(null);
            },
          },
          {
            key: 'openSharePoint',
            text: 'Open in SharePoint',
            iconProps: { iconName: 'OpenInNewWindow' },
            onClick: () => {
              openFile(item);
              setContextMenu(null);
            },
          },
          {
            key: 'favorite',
            text: favorite ? 'Remove from Favorites' : 'Add to Favorites',
            iconProps: { iconName: favorite ? 'FavoriteStarFill' : 'FavoriteStar' },
            onClick: () => {
              if (favorite) void removeFolderFavorite(item.path);
              else void addFolderFavorite(item);
              setContextMenu(null);
            },
          },
          {
            key: 'move',
            text: 'Move to',
            iconProps: { iconName: 'MoveToFolder' },
            onClick: () => {
              openMoveDialog(
                selectedItems.has(item.path) && selectedItems.size > 1
                  ? filteredItems.filter((i) => selectedItems.has(i.path))
                  : [item],
              );
              setContextMenu(null);
            },
          },
          {
            key: 'copyLink',
            text: 'Copy Link',
            iconProps: { iconName: 'Link' },
            onClick: () => {
              copyLink(item);
              setContextMenu(null);
            },
          },
          {
            key: 'rename',
            text: 'Rename',
            iconProps: { iconName: 'Rename' },
            onClick: () => {
              openRenameDialog(item);
              setContextMenu(null);
            },
          },
          {
            key: 'delete',
            text: 'Delete',
            iconProps: { iconName: 'Delete' },
            onClick: () => {
              setContextMenu(null);
              showDeleteConfirm(item);
            },
          },
        ];
      }
      return [
        {
          key: 'preview',
          text: 'Preview',
          iconProps: { iconName: 'View' },
          onClick: () => {
            setPreviewItem(item);
            setContextMenu(null);
          },
        },
        {
          key: 'open',
          text: 'Open in SharePoint',
          iconProps: { iconName: 'OpenInNewWindow' },
          onClick: () => {
            openFile(item);
            setContextMenu(null);
          },
        },
        {
          key: 'download',
          text: 'Download',
          iconProps: { iconName: 'Download' },
          onClick: () => {
            downloadItem(item);
            setContextMenu(null);
          },
        },
        {
          key: 'move',
          text: 'Move to',
          iconProps: { iconName: 'MoveToFolder' },
          onClick: () => {
            openMoveDialog(
              selectedItems.has(item.path) && selectedItems.size > 1
                ? filteredItems.filter((i) => selectedItems.has(i.path))
                : [item],
            );
            setContextMenu(null);
          },
        },
        {
          key: 'copyLink',
          text: 'Copy Link',
          iconProps: { iconName: 'Link' },
          onClick: () => {
            copyLink(item);
            setContextMenu(null);
          },
        },
        {
          key: 'favorite',
          text: favoritePaths.has(item.path) ? 'Remove from Favorites' : 'Add to Favorites',
          iconProps: {
            iconName: favoritePaths.has(item.path) ? 'FavoriteStarFill' : 'FavoriteStar',
          },
          onClick: () => {
            if (favoritePaths.has(item.path)) void removeFolderFavorite(item.path);
            else void addFolderFavorite(item);
            setContextMenu(null);
          },
        },
        {
          key: 'rename',
          text: 'Rename',
          iconProps: { iconName: 'Rename' },
          onClick: () => {
            openRenameDialog(item);
            setContextMenu(null);
          },
        },
        {
          key: 'delete',
          text: 'Delete',
          iconProps: { iconName: 'Delete' },
          onClick: () => {
            setContextMenu(null);
            showDeleteConfirm(item);
          },
        },
      ];
    },
    [
      toggleExpand,
      setPreviewItem,
      openFile,
      openMoveDialog,
      openRenameDialog,
      downloadItem,
      copyLink,
      showDeleteConfirm,
      currentSiteId,
      folderFavorites,
      favoritePaths,
      addFolderFavorite,
      removeFolderFavorite,
      selectedItems,
      filteredItems,
    ],
  );

  // Keep the user's place when sorting (Explorer-style): record an anchor row's
  // on-screen position before the re-sort, then scroll so it lands at the same
  // spot afterward. listRef wraps the list so we can reach the scroll container.
  const listRef = useRef<HTMLDivElement>(null);
  const detailsListRef = useRef<IDetailsList>(null);
  // The anchor for a sort: keep the selected row at its on-screen offset, or
  // scroll to the top when nothing is selected (Explorer behavior).
  const sortAnchor = useRef<{ kind: 'row'; path: string; offset: number } | { kind: 'top' } | null>(
    null,
  );

  const getScrollContainer = useCallback((): HTMLElement | null => {
    const root = listRef.current;
    if (!root) return null;
    const pane = root.querySelector('.ms-ScrollablePane--contentContainer') as HTMLElement | null;
    if (pane) return pane;
    // Fallback: walk up from a row to the nearest scrollable ancestor, in case
    // the Fluent class name differs across versions.
    let el = root.querySelector('[data-path]') as HTMLElement | null;
    while (el && el !== root.parentElement) {
      if (el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    return root;
  }, []);

  // Top offset of a row (by path) within the scroll container, or null if the
  // row is not currently rendered (virtualized away).
  const rowOffsetTop = useCallback((container: HTMLElement, path: string): number | null => {
    const top = container.getBoundingClientRect().top;
    for (const el of Array.from(container.querySelectorAll('[data-path]'))) {
      if (el.getAttribute('data-path') === path) {
        return el.getBoundingClientRect().top - top;
      }
    }
    return null;
  }, []);

  const handleColumnSort = useCallback(
    (_ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
      if (!column) return;
      const key = column.key === 'name' ? 'name' : column.key === 'modified' ? 'date' : null;
      if (!key) return;
      // Capture the anchor before the re-sort: the selected row (kept in place)
      // if one is on screen, otherwise scroll to the top.
      const container = getScrollContainer();
      const selected = container
        ? (Array.from(container.querySelectorAll('[data-path]')) as HTMLElement[]).find((r) =>
            selectedItems.has(r.getAttribute('data-path') || ''),
          )
        : null;
      const path = selected?.getAttribute('data-path') || null;
      const offset = path && container ? rowOffsetTop(container, path) : null;
      sortAnchor.current = path && offset != null ? { kind: 'row', path, offset } : { kind: 'top' };
      if (sortBy === key) {
        toggleSortOrder();
      } else {
        setSortBy(key);
      }
    },
    [sortBy, setSortBy, toggleSortOrder, getScrollContainer, rowOffsetTop, selectedItems],
  );

  // Restore the anchor after a sort. useLayoutEffect runs before the browser
  // paints, so when the reordered rows are already in the DOM the scroll is
  // corrected with NO flicker. If the virtualized list has not repositioned yet,
  // fall back to retrying across animation frames (and scroll the row into view
  // by index if it moved out of the rendered range entirely).
  useLayoutEffect(() => {
    const anchor = sortAnchor.current;
    if (!anchor) return;
    sortAnchor.current = null;

    const applyOnce = (): boolean => {
      const container = getScrollContainer();
      if (!container) return true; // nothing to do
      if (anchor.kind === 'top') {
        container.scrollTop = 0;
        return true;
      }
      const newOffset = rowOffsetTop(container, anchor.path);
      if (newOffset == null) return false; // row not rendered yet
      container.scrollTop += newOffset - anchor.offset;
      return true;
    };

    if (applyOnce()) return; // synchronous, no flicker
    // Async fallback for the rare case the list has not laid out yet.
    let frames = 0;
    const tick = () => {
      if (applyOnce()) return;
      if (frames++ < 8) {
        requestAnimationFrame(tick);
        return;
      }
      const idx = visibleRows.findIndex((r) => r.item.path === (anchor as { path: string }).path);
      if (idx >= 0)
        detailsListRef.current?.focusIndex?.(idx, false, undefined, ScrollToMode.center);
    };
    requestAnimationFrame(tick);
  }, [visibleRows, getScrollContainer, rowOffsetTop]);

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: 'name',
        name: 'Name',
        fieldName: 'name',
        minWidth: 60,
        isResizable: true,
        onColumnClick: handleColumnSort,
        isSorted: sortBy === 'name',
        isSortedDescending: sortBy === 'name' && sortOrder === 'desc',
        // Explorer order in tree mode: indent, then chevron, then the icon, then
        // the name. Search mode is flat: just icon then name (no chevron/indent).
        onRender: (item: BlobItem) => {
          const meta = rowMeta.get(item.path);
          const depth = meta?.depth ?? 0;
          const isExpanded = meta?.isExpanded ?? false;
          const isLoadingChildren = loadingPaths.has(item.path);
          return (
            <span
              className="tree-name"
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingLeft: depth * 12,
              }}
            >
              {item.isFolder ? (
                isLoadingChildren ? (
                  // Children loading: spinner in place of the chevron.
                  <span
                    style={{
                      width: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Spinner size={SpinnerSize.xSmall} />
                  </span>
                ) : (
                  <span
                    data-selection-disabled
                    role="button"
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    onClick={(e) => {
                      e.stopPropagation();
                      void toggleExpand(item.path);
                    }}
                    style={{
                      width: 16,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon
                      iconName={isExpanded ? 'ChevronDownMed' : 'ChevronRightMed'}
                      style={{ fontSize: 12 }}
                    />
                  </span>
                )
              ) : (
                <span style={{ width: 16 }} />
              )}
              <span style={{ display: 'flex', alignItems: 'center', marginLeft: 6 }}>
                <FileThumbnail item={item} size={16} />
              </span>
              <span
                className="file-name-column"
                style={{ display: 'flex', flexDirection: 'column', marginLeft: 8, minWidth: 0 }}
              >
                <span className="file-name" title={item.name}>
                  {item.name}
                </span>
                {inFavorites && depth === 0 && parentFolderPath(item.path) !== '' && (
                  <span className="file-favorite-location">
                    in {parentFolderPath(item.path).replace(/\/+$/, '')}
                  </span>
                )}
              </span>
            </span>
          );
        },
      },
      {
        key: 'indicators',
        name: '',
        minWidth: 36,
        maxWidth: 36,
        onRender: (item: BlobItem) => (
          <span
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}
          >
            {favoritePaths.has(item.path) && (
              <Icon
                iconName="FavoriteStarFill"
                role="img"
                aria-label="Favorited"
                title="Favorited"
                style={{ fontSize: 12, color: 'var(--favorite-star, #eab308)' }}
              />
            )}
            <AttachmentIndicator value={item.hasAttachments} />
          </span>
        ),
      },
      {
        key: 'modified',
        name: 'Date',
        minWidth: 90,
        maxWidth: 90,
        onColumnClick: handleColumnSort,
        isSorted: sortBy === 'date',
        isSortedDescending: sortBy === 'date' && sortOrder === 'desc',
        onRender: (item: BlobItem) => {
          const display = getItemDateDisplay(item);
          return display ? (
            <span className="file-date" title={formatItemDateTooltip(display)}>
              {formatDate(display.date)}
            </span>
          ) : null;
        },
      },
      {
        key: 'actions',
        name: '',
        minWidth: 32,
        maxWidth: 32,
        onRender: (item: BlobItem) => (
          // data-selection-disabled: clicking the kebab must not toggle the
          // row selection.
          <span data-selection-disabled>
            <IconButton
              iconProps={{ iconName: 'More' }}
              title="Actions"
              ariaLabel="Actions"
              menuIconProps={{ iconName: '' }}
              menuProps={{ items: getMenuItems(item) }}
              styles={{
                root: { height: 28, width: 28, padding: 0 },
                // The three-dot glyph reads fainter than fuller glyphs at the
                // same size, so it gets the primary text color, not theme blue.
                icon: { fontSize: 16, color: 'var(--text-primary)' },
                iconHovered: { color: 'var(--text-primary)' },
                iconPressed: { color: 'var(--text-primary)' },
                iconExpanded: { color: 'var(--text-primary)' },
                menuIcon: { display: 'none', width: 0, margin: 0 },
                rootHasMenu: { width: 28 },
              }}
            />
          </span>
        ),
      },
    ],
    [
      getMenuItems,
      sortBy,
      sortOrder,
      handleColumnSort,
      rowMeta,
      toggleExpand,
      loadingPaths,
      favoritePaths,
      inFavorites,
    ],
  );

  // Row wrapper carries data-path / data-isfolder so DropZone can hit-test the
  // folder under the cursor during a drag. The drop highlight is applied
  // imperatively by DropZone (toggling .drag-folder-target), not here, so this
  // wrapper has no React drag state and never re-renders rows on a drag.
  const onRenderRow = useCallback(
    (
      props?: IDetailsRowProps,
      defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null,
    ): JSX.Element | null => {
      if (!props || !defaultRender) return null;
      const item = props.item as BlobItem;
      return (
        <div data-path={item.path} data-isfolder={item.isFolder ? '1' : '0'}>
          {defaultRender(props)}
        </div>
      );
    },
    [],
  );

  // Slim sticky header: 30px tall, 11px labels, select-all check centered
  // (the default header is 42px with the check pinned to its own metrics).
  const onRenderDetailsHeader = useCallback(
    (
      props?: IDetailsHeaderProps,
      defaultRender?: (props?: IDetailsHeaderProps) => JSX.Element | null,
    ): JSX.Element | null => {
      if (!props || !defaultRender) return null;
      return (
        <Sticky stickyPosition={StickyPositionType.Header}>
          {defaultRender({
            ...props,
            styles: {
              root: {
                height: 32,
                paddingTop: 0,
                selectors: {
                  // Heights only. The check cell keeps Fluent's inline-flex -
                  // forcing display:flex made it block-level (full row width)
                  // and wrapped the column labels out of view.
                  '.ms-DetailsHeader-cell': { height: 32 },
                  '.ms-DetailsHeader-cellIsCheck': { height: 32 },
                  '.ms-DetailsHeader-cellTitle': {
                    height: 32,
                    alignItems: 'center',
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                  '.ms-DetailsHeader-cellName': { fontSize: 11 },
                },
              },
            },
          })}
        </Sticky>
      );
    },
    [],
  );

  // Initial loading state (no items yet)
  if ((isLoading || treeLoading) && filteredItems.length === 0) {
    return (
      <div className="file-list loading">
        <Spinner size={SpinnerSize.large} label="Loading" />
      </div>
    );
  }

  // Error state
  if (error && filteredItems.length === 0) {
    return (
      <div className="file-list error">
        <Text className="error-text">{error}</Text>
      </div>
    );
  }

  // Empty state
  if (filteredItems.length === 0) {
    return (
      <div className="file-list empty">
        <Text className="empty-text">
          {inFavorites
            ? 'No favorite folders for this site.'
            : isSearching
              ? 'No matches.'
              : 'This site has no files yet.'}
        </Text>
        <Text className="empty-hint">
          {inFavorites
            ? 'Use a folder’s Actions menu to add one.'
            : 'Drag and drop emails or files onto a folder.'}
        </Text>
      </div>
    );
  }

  // List view - Fluent DetailsList in a ScrollablePane (sticky header,
  // built-in selection mechanics, keyboard navigation, ARIA grid).
  return (
    <div className="file-list details" ref={listRef}>
      {/* Loading overlay when refreshing */}
      {isLoading && (
        <div className="loading-overlay">
          <Spinner size={SpinnerSize.medium} label="Loading" />
        </div>
      )}

      <ScrollablePane>
        <DetailsList
          componentRef={detailsListRef}
          items={filteredItems}
          columns={columns}
          getKey={(item: BlobItem) => item.path}
          setKey="files"
          compact
          selection={selection}
          selectionMode={SelectionMode.multiple}
          selectionPreservedOnEmptyClick
          checkboxVisibility={CheckboxVisibility.always}
          layoutMode={DetailsListLayoutMode.justified}
          cellStyleProps={{ cellLeftPadding: 8, cellRightPadding: 4, cellExtraRightPadding: 0 }}
          isHeaderVisible
          onItemInvoked={handleItemInvoked}
          onItemContextMenu={(item?, _index?, ev?) => {
            const e = ev as MouseEvent | undefined;
            if (!item || !e) return;
            e.preventDefault();
            setContextMenu({
              item: item as BlobItem,
              target: { x: e.clientX, y: e.clientY },
            });
          }}
          onRenderRow={onRenderRow}
          onRenderDetailsHeader={onRenderDetailsHeader}
          ariaLabelForSelectAllCheckbox="Select all"
          checkButtonAriaLabel="Select item"
        />
      </ScrollablePane>

      {/* Row Context Menu */}
      {contextMenu && (
        <ContextualMenu
          items={getMenuItems(contextMenu.item)}
          target={contextMenu.target}
          onDismiss={() => setContextMenu(null)}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Item"
        message={`Are you sure you want to delete "${confirmDialog.item?.name}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

// Memoized: the parent (App) re-renders on upload-progress and notification
// updates; FileList takes no props, so memo keeps those from cascading into a
// full tree re-render. It re-renders only when its own selectors change.
export const FileList = React.memo(FileListInner);
